/**
 * KillSwitchStatusBar — compact inline kill switch status for the Trading page header.
 *
 * Shows a single-line summary of both kill switch states and a Configure button
 * that opens the full KillSwitchPanel in a modal.
 */

import { useQuery } from '@tanstack/react-query';
import { getKillSwitchSettings } from '../../api/client';

const KILL_SWITCH_KEY = ['kill-switch-settings'] as const;

interface KillSwitchStatusBarProps {
  onConfigure: () => void;
}

interface StatusBadgeProps {
  label: string;
  enabled: boolean;
  ddPercent: number;
}

function StatusBadge({ label, enabled, ddPercent }: StatusBadgeProps) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${enabled ? 'bg-green-400' : 'bg-red-500'}`}
        aria-hidden="true"
      />
      <span className="text-gray-300">{label}:</span>
      <span className={`font-medium ${enabled ? 'text-green-400' : 'text-red-400'}`}>
        {enabled ? `On (${ddPercent}%)` : 'Off'}
      </span>
    </span>
  );
}

export function KillSwitchStatusBar({ onConfigure }: KillSwitchStatusBarProps) {
  const { data, isLoading } = useQuery({
    queryKey: KILL_SWITCH_KEY,
    queryFn: getKillSwitchSettings,
    staleTime: 60_000,
  });

  return (
    <div className="flex items-center gap-3 text-xs min-w-0">
      <span className="text-gray-500 font-medium shrink-0">Kill Switch</span>
      <span className="text-gray-600">|</span>

      {isLoading || !data ? (
        <span className="text-gray-500 italic">loading...</span>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="PT" enabled={data.pt.enabled} ddPercent={data.pt.ddPercent} />
          <span className="text-gray-700">|</span>
          <StatusBadge label="LT" enabled={data.lt.enabled} ddPercent={data.lt.ddPercent} />
        </div>
      )}

      <button
        type="button"
        onClick={onConfigure}
        className="ml-auto shrink-0 flex items-center gap-1 px-2 py-0.5 text-xs text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded transition-colors"
        aria-label="Configure kill switch settings"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Configure
      </button>
    </div>
  );
}
